import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const DIAMETER_VECTORS = [
  { x: 5, y: -4 },
  { x: 4, y: 3 },
  { x: 3, y: -4 },
  { x: 6, y: 2 },
  { x: 5, y: 2 },
  { x: 3, y: 5 },
];

function pick(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

function shiftedSquare(variable, value) {
  if (value === 0) return `${variable}^2`;
  return value > 0
    ? `(${variable}-${value})^2`
    : `(${variable}+${Math.abs(value)})^2`;
}

function equationLatex(center, radiusSquared) {
  return `${shiftedSquare("x", center.x)}+${shiftedSquare("y", center.y)}=${radiusSquared}`;
}

function pointLatex(point) {
  return `(${point.x},${point.y})`;
}

function wrapNegative(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function makeOptions(center, radiusSquared) {
  const correct = equationLatex(center, radiusSquared);
  const candidates = [
    correct,
    equationLatex({ x: center.x + 4, y: -center.y - 1 }, radiusSquared),
    equationLatex({ x: center.x, y: -center.y }, radiusSquared),
    equationLatex({ x: center.x + 4, y: center.y - 1 }, radiusSquared),
  ];

  return shuffle(candidates).map((latex, index) => ({
    label: LETTERS[index],
    latex,
    isCorrect: latex === correct,
  }));
}

export function createTask({ center, vector, source, fixedOptions }) {
  const pointA = { x: center.x - vector.x, y: center.y - vector.y };
  const pointB = { x: center.x + vector.x, y: center.y + vector.y };
  const radiusSquared = vector.x ** 2 + vector.y ** 2;
  const options = fixedOptions || (source?.variant === "original" && source.session === "2026_CZE"
    ? [
      { label: "A", latex: "(x-1)^2+(y-1)^2=8", isCorrect: false },
      { label: "B", latex: "x^2+(y-2)^2=2", isCorrect: false },
      { label: "C", latex: "(x-2)^2+y^2=2", isCorrect: true },
      { label: "D", latex: "(x-2)^2+y^2=8", isCorrect: false },
    ]
    : source?.variant === "original"
    ? [
      { label: "A", latex: "(x-1)^2+(y-3)^2=41", isCorrect: true },
      { label: "B", latex: "(x-5)^2+(y+4)^2=41", isCorrect: false },
      { label: "C", latex: "(x-1)^2+(y+3)^2=41", isCorrect: false },
      { label: "D", latex: "(x-5)^2+(y-4)^2=41", isCorrect: false },
    ]
    : makeOptions(center, radiusSquared));

  return {
    type: "MaturaCircleDiameterEquationClosedTask",
    data: {
      pointA,
      pointB,
      center,
      vector,
      radiusSquared,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
      maxPoints: 1,
    },
    ...(source ? { source } : {}),
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant !== "original" && generationOptions.includeOriginal && Math.floor(Math.random() * 217) === 0) {
    return generateTask({ variant: "original", session: "2026_CZE" });
  }
  if (generationOptions.variant === "original" && generationOptions.session === "2026_CZE") {
    return createTask({ center: { x: 2, y: 0 }, vector: { x: 1, y: -1 },
      source: { variant: "original", exam: "MATURA", session: "2026_CZE", taskNumber: 24 } });
  }
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2024_SIE"
  ) {
    return createTask({
      center: { x: 1, y: 3 },
      vector: { x: 5, y: -4 },
      source: {
        variant: "original",
        exam: "MATURA",
        session: "2024_SIE",
        taskNumber: 23,
      },
    });
  }

  const center = {
    x: pick([-3, -2, -1, 1, 2, 3]),
    y: pick([-3, -2, -1, 1, 2, 3]),
  };
  return createTask({ center, vector: pick(DIAMETER_VECTORS) });
}

export function renderTask({ data }) {
  const { pointA, pointB, options } = data;
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne - zamknięte (0-1).</strong></p>
      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math="(x,y)" /> odcinek o końcach{" "}
        <InlineMath math={`A=${pointLatex(pointA)}`} /> oraz <InlineMath math={`B=${pointLatex(pointB)}`} />
        {" "}jest średnicą okręgu <InlineMath math="O" />.
      </p>
      <p><strong>Dokończ zadanie. Wybierz właściwą odpowiedź spośród podanych.</strong></p>
      <p>Okrąg <InlineMath math="O" /> jest określony równaniem</p>
      {options.map((option) => (
        <div key={option.label} style={{ marginBottom: 8 }}>
          <InlineMath math={`${option.label}.\\;${option.latex}`} />
        </div>
      ))}
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex = typeof userAnswer?.choiceIndex === "number" ? userAnswer.choiceIndex : null;
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ value: index, label: option.label, textNode: option.label }))}
      value={choiceIndex}
      onChange={(choiceIndexNext) => {
        const option = data.options[choiceIndexNext];
        onChange?.({ choiceIndex: choiceIndexNext, label: option.label, latex: option.latex });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return typeof userAnswer?.choiceIndex === "number" && userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return <InlineMath math={`${option.label}.\\;${option.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.label || !userAnswer?.latex) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${userAnswer.label}.\\;${userAnswer.latex}`} />;
}

export function renderExplanation({ data }) {
  const { pointA, pointB, center, radiusSquared } = data;
  const radiusDx = Math.abs(pointA.x - center.x);
  const radiusDy = Math.abs(pointA.y - center.y);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Środek okręgu jest środkiem jego średnicy, więc liczymy środek odcinka <InlineMath math="AB" />:
      </p>
      <BlockMath math={`x_S=\\frac{${wrapNegative(pointA.x)}+${wrapNegative(pointB.x)}}{2}=${center.x}`} />
      <BlockMath math={`y_S=\\frac{${wrapNegative(pointA.y)}+${wrapNegative(pointB.y)}}{2}=${center.y}`} />
      <BlockMath math={`S=(${center.x},${center.y})`} />
      <p>
        <strong>Krok 2.</strong> Promień jest odległością od środka <InlineMath math="S" /> do punktu <InlineMath math="A" />:
      </p>
      <BlockMath math={`r=|SA|=\\sqrt{${radiusDx}^2+${radiusDy}^2}=\\sqrt{${radiusSquared}}`} />
      <p>
        Ponieważ jest to zadanie zamknięte, wszystkie odpowiedzi mają tę samą prawą stronę. Do wyboru poprawnej odpowiedzi wystarcza więc poprawnie wyznaczony środek.
      </p>
      <BlockMath math={equationLatex(center, radiusSquared)} />
    </div>
  );
}

export default { generateTask, renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };
